<script setup>
// @ts-check
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import FileThumbnail from '@/components/file/FileThumbnail.vue'
import { useAttachments } from '@/core/composables/useAttachments'
import { computed, toRefs } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  loading: {
    type: Boolean
  },
  hasError: {
    type: Boolean
  },
  files: {
    /** @type {import('vue').PropType<Attachments>} */
    type: Array,
    required: true
  },
  carouselHeight: {
    type: Number,
    default: 422
  }
})

const store = useStore()

defineOptions({
  name: 'FileCarousel'
})

const { files } = toRefs(props)

const { viewImages } = useAttachments(files)

const isLg = computed(() => store.getters['app/isLg'])

const showControls = computed(() => files.value.length > 1)

const arrowsType = computed(() => {
  if (!showControls.value) {
    return false
  }

  return isLg.value ? 'hover' : true
})
</script>

<template>
  <div class="flex flex-col items-center">
    <div
      v-if="loading"
      class="flex w-full items-center justify-center"
      :style="`height:${carouselHeight}px`"
    >
      <LoadingSpinner :size="16" />
    </div>
    <div
      v-else-if="hasError"
      class="flex min-h-40 w-full items-center justify-center rounded-md bg-secondary-100 p-4"
    >
      <VIcon
        icon="$alert-circle-outline"
        size="large"
        class="mr-3 text-danger"
      />
      <span class="font-medium">{{ $t('files.preview_error') }}</span>
    </div>
    <VCarousel
      v-else-if="files.length"
      :height="carouselHeight"
      :show-arrows="arrowsType"
      :hide-delimiters="!showControls"
      hide-delimiter-background
      color="primary"
    >
      <VCarouselItem
        v-for="(file, i) in files"
        :key="i"
        :height="carouselHeight"
      >
        <div
          class="carousel-item-content"
          :class="{
            'controls-visible': showControls
          }"
        >
          <div class="carousel-item-content__inner">
            <FileThumbnail
              class="size-full"
              :attachment="file"
              @click-image="viewImages(file.id)"
            />
          </div>
        </div>
      </VCarouselItem>
    </VCarousel>
    <div
      v-else
      class="flex min-h-40 w-full items-center justify-center rounded-md bg-secondary-100"
    >
      <VIcon icon="$image-outline" size="large" class="mr-3 text-warning" />
      <span class="font-medium">{{ $t('no_attachments') }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

:deep() {
  .v-btn--icon .v-icon {
    --v-icon-size-multiplier: 1;
  }

  .v-window {
    max-width: 520px;

    &__controls {
      padding: 0 0 36px 0;
    }

    &__right {
      margin-right: -20px;

      .v-icon {
        margin-right: 5px;
      }
    }

    &__left {
      margin-left: -20px;

      .v-icon {
        margin-left: 5px;
      }
    }
  }

  .v-carousel__controls {
    height: 24px;

    &__item {
      margin: 0 4px;
    }

    .v-btn--size-x-small {
      font-size: 8px !important;
      height: 24px !important;
      width: 24px !important;
    }
  }

  .carousel-item-content {
    @apply h-full;

    &.controls-visible {
      padding-bottom: 36px;
    }

    &__inner {
      @apply flex items-center justify-center;
      @apply bg-secondary-100 rounded-md;
      @apply h-full;
      padding: 0;
    }
  }
}
</style>
